import { AUTH_BASE_URL } from '../config/constants';

/**
 * Service unifié de gestion de session utilisateur
 * Gère l'authentification utilisateur + Twitch + Spotify via JWT unifié en cookies httpOnly
 */
class UserSessionService {
  constructor() {
    this.user = null;
    this.isAuthenticated = false;
    this._isLoading = true;
    /** Promesse en cours pour éviter les appels verify() concurrents (multi-montage, Strict Mode) */
    this._verifyPromise = null;
  }

  /**
   * Initialise le service depuis l'URL (callback OAuth)
   * Nettoie les paramètres d'URL après authentification
   */
  initFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const authParam = urlParams.get('auth');

    if (authParam === 'success') {
      // Nettoyer l'URL (le token est maintenant dans un cookie httpOnly)
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('auth');
      newUrl.searchParams.delete('platform');
      newUrl.searchParams.delete('token');
      window.history.replaceState(
        {},
        document.title,
        newUrl.pathname + newUrl.search
      );

      return true;
    }

    return false;
  }

  /**
   * Vérifie le token JWT auprès du serveur (via cookie httpOnly).
   * Les appels concurrents partagent la même requête pour limiter le nombre d'appels.
   * @returns {Promise<Object|null>} Informations utilisateur ou null
   */
  async verify() {
    if (this._verifyPromise) {
      return this._verifyPromise;
    }
    this._verifyPromise = this._doVerify();
    try {
      return await this._verifyPromise;
    } finally {
      this._verifyPromise = null;
    }
  }

  async _doVerify() {
    try {
      const response = await fetch(`${AUTH_BASE_URL}/auth/verify`, {
        method: 'GET',
        credentials: 'include', // Important pour envoyer les cookies
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          this.user = data.user;
          this.isAuthenticated = true;
          this._isLoading = false;
          return this.user;
        }
        // Si success: false, l'utilisateur n'est pas authentifié (comportement attendu)
        // Pas besoin de logger, c'est un comportement normal
      } else {
        // Autres erreurs HTTP (500, 503, etc.)
        console.error(
          `Erreur lors de la vérification de la session: ${response.status} ${response.statusText}`
        );
      }
    } catch (error) {
      // Erreur réseau (pas de réponse du serveur)
      console.error(
        'Erreur réseau lors de la vérification de la session:',
        error
      );
    }

    // En cas d'erreur ou de token invalide
    this.user = null;
    this.isAuthenticated = false;
    this._isLoading = false;
    return null;
  }

  /**
   * Déconnecte l'utilisateur
   */
  async logout() {
    try {
      await fetch(`${AUTH_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le localStorage
      const { storage } = await import('../utils/storage');
      storage.clearAuth();

      this.user = null;
      this.isAuthenticated = false;
      this._isLoading = false;
    }
  }

  /**
   * Vérifie si l'utilisateur est authentifié
   * @returns {boolean} True si authentifié
   */
  isUserAuthenticated() {
    return this.isAuthenticated;
  }

  /**
   * Récupère les informations utilisateur
   * @returns {Object|null} Informations utilisateur ou null
   */
  getUser() {
    return this.user;
  }

  /**
   * Vérifie si le service est en cours de chargement
   * @returns {boolean} True si en cours de chargement
   */
  isLoading() {
    return this._isLoading;
  }

  /**
   * Vérifie si Twitch est connecté
   * @returns {boolean} True si Twitch est connecté
   */
  isTwitchConnected() {
    return this.user?.twitch?.connected || false;
  }

  /**
   * Vérifie si Spotify est connecté
   * @returns {boolean} True si Spotify est connecté
   */
  isSpotifyConnected() {
    return this.user?.spotify?.connected || false;
  }

  /**
   * Récupère les informations Twitch
   * @returns {Object|null} Informations Twitch ou null
   */
  getTwitchUser() {
    return this.user?.twitch?.userData || null;
  }

  /**
   * Récupère les informations Spotify
   * @returns {Object|null} Informations Spotify ou null
   */
  getSpotifyUser() {
    return this.user?.spotify?.userData || null;
  }

  /**
   * Démarre la connexion Twitch
   * Redirige vers l'endpoint OAuth Twitch
   */
  connectTwitch() {
    if (!this.isAuthenticated) {
      throw new Error(
        "Vous devez d'abord vous connecter avec un compte utilisateur"
      );
    }
    window.location.href = `${AUTH_BASE_URL}/auth/user-twitch?userId=${this.user.id}`;
  }

  /**
   * Démarre la connexion Spotify
   * Redirige vers l'endpoint OAuth Spotify
   */
  connectSpotify() {
    if (!this.isAuthenticated) {
      throw new Error(
        "Vous devez d'abord vous connecter avec un compte utilisateur"
      );
    }
    window.location.href = `${AUTH_BASE_URL}/auth/user-spotify?userId=${this.user.id}`;
  }

  /**
   * Déconnecte Twitch
   */
  async disconnectTwitch() {
    if (!this.isAuthenticated) {
      return;
    }

    try {
      await fetch(`${AUTH_BASE_URL}/auth/twitch/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      // Rafraîchir la session pour mettre à jour les infos
      await this.verify();
    } catch (error) {
      console.error('Erreur lors de la déconnexion Twitch:', error);
    }
  }

  /**
   * Déconnecte Spotify
   */
  async disconnectSpotify() {
    if (!this.isAuthenticated) {
      return;
    }

    try {
      await fetch(`${AUTH_BASE_URL}/auth/spotify/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      // Rafraîchir la session pour mettre à jour les infos
      await this.verify();
    } catch (error) {
      console.error('Erreur lors de la déconnexion Spotify:', error);
    }
  }

  /**
   * Rafraîchit la session utilisateur
   * Utile après une connexion/déconnexion Twitch/Spotify
   */
  async refreshSession() {
    return await this.verify();
  }
}

// Instance singleton
const userSessionService = new UserSessionService();

export default userSessionService;
